import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import UnifiedLeadsPage from "./pages/UnifiedLeadsPage";
import CalendarPage from "./pages/CalendarPage";
import LeadMarketPage from "./pages/LeadMarketPage";
import ChallengesPage from "./pages/ChallengesPage";
import UserManagement from "./pages/UserManagement";
import InvitationsPage from "@/pages/InvitationsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import TeamOverviewPage from "./pages/TeamOverviewPage";
import ScoreBoardPage from "./pages/ScoreBoardPage";
import PublicFormPage from "./pages/public-form";
import CustomerReferralPage from "./pages/CustomerReferralPage";
import PerformanceMetricsPage from "./pages/PerformanceMetricsPage";
import { Chatbot } from "./components/chatbot";
import { useEffect } from "react";

// Force cache bust - updated for latest deployment

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center h-screen">Laden...</div>;

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin Route Wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center h-screen">Laden...</div>;

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.role !== "admin" && currentUser.role !== "developer") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// App Component with Protected Routes
function AppWithRoutes() {
  const { currentUser } = useAuth();

  // Check if we should show the chatbot (only on protected routes for authenticated users)
  const shouldShowChatbot = currentUser && !['/login', '/register', '/forgot-password'].includes(window.location.pathname) && !window.location.pathname.includes('/form/');

  // Redirect to dashboard if logged in and trying to access login/register pages
  useEffect(() => {
    if (currentUser && window.location.pathname === "/login") {
      window.history.replaceState(null, "", "/dashboard");
    }
    // Additional check for / path to redirect to /dashboard if logged in
    if (currentUser && window.location.pathname === "/") {
      window.history.replaceState(null, "", "/dashboard");
    }
  }, [currentUser]);

  return (
    <>
      <Routes>
        <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/login" element={currentUser ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/register" element={currentUser ? <Navigate to="/dashboard" replace /> : <Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Public Form Route */}
        <Route path="/form/:formId" element={<PublicFormPage />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/customer-referral" element={<ProtectedRoute><CustomerReferralPage /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><UnifiedLeadsPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/lead-market" element={<ProtectedRoute><LeadMarketPage /></ProtectedRoute>} />
        <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
        <Route path="/scoreboard" element={<ProtectedRoute><ScoreBoardPage /></ProtectedRoute>} />
        <Route path="/invitations" element={<ProtectedRoute><InvitationsPage /></ProtectedRoute>} />
        <Route path="/performance-metrics" element={<AdminRoute><PerformanceMetricsPage /></AdminRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

        {/* Admin Routes */}
        <Route path="/user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
        <Route path="/team-overview" element={<AdminRoute><TeamOverviewPage /></AdminRoute>} />

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Chatbot - only show on protected routes */}
      {shouldShowChatbot && <Chatbot />}
    </>
  );
}

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <AppWithRoutes />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
